import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import type { BasePagination } from "../../../types/api.ts"
import { getAttachments } from "../../Album/api/attachment.services.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import type { AttachmentListData } from "../../Album/api/services.types.ts"
import { resolveDateBounds } from "../../Files/helpers/date-range-filter.ts"
import {
  mapAttachmentToFileItem,
  mapDocumentToFileItem
} from "../../Files/helpers/map-to-file-item.ts"
import {
  documentOrderingFor,
  photoOrderingFor
} from "../../Files/helpers/server-ordering.ts"
import { sortFileItems } from "../../Files/helpers/sort-file-items.ts"
import type { FileItem, FileRights } from "../../Files/types/file-item.types.ts"
import { type FilesSortOrder } from "../../Files/types/files-filters.types.ts"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import type {
  ProjectDocument,
  RelatedRecordType
} from "../../Project/types/project-documents.types.ts"
import type {
  GlobalFilesAttention,
  GlobalFilesLocation
} from "../types/global-files-filters.types.ts"

const PAGE_SIZE = 25

/** Which of the two stores this fetch draws from — the same hook backs All
 * Files (both), Business Documents (documents only) and Photos & Media
 * (photos only), rather than three near-duplicate data layers. */
type GlobalFilesSource = "all" | "documents" | "photos"

interface GlobalFilesDataFilters {
  q: string
  documentType: string | null
  status: string | null
  relatedRecordType: RelatedRecordType | null
  location: GlobalFilesLocation | null
  // A specific Project/Lead id narrowing `location` further — sent as
  // `lead` to the documents store and `project` to the photos store (the
  // same underlying `core.Project` row, two different field names on the
  // two backend filtersets).
  recordId: string | null
  // Comma-joined `crm.Customer` ids — "uploaded by this specific customer".
  customerIds: string[]
  dateRange: Parameters<typeof resolveDateBounds>[0]["date_range"]
  dateAfter: string | null
  dateBefore: string | null
  // Document Health drill-through — sent verbatim to both stores; the
  // backend (not this hook) decides that Photos only ever answers
  // `missing_document_type` for the other categories.
  attention: GlobalFilesAttention | null
}

const EMPTY_FILTERS: GlobalFilesDataFilters = {
  q: "",
  documentType: null,
  status: null,
  relatedRecordType: null,
  location: null,
  recordId: null,
  customerIds: [],
  dateRange: null,
  dateAfter: null,
  dateBefore: null,
  attention: null
}

// Both endpoints paginate on their own — nothing here ever asks for "every
// file". A combined "page" is one page from each active store, sorted
// together client-side, since there is no single server-side list spanning
// both models (see `docs/kb` — Documents and Photos stay two stores, not a
// merged one).

export function useGlobalFilesData(
  source: GlobalFilesSource,
  filters: GlobalFilesDataFilters = EMPTY_FILTERS,
  sort: FilesSortOrder = "modified_desc"
) {
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)
  const moduleDocs = useAppSelector(
    (state) => state.auth?.permissions?.codes?.projects?.docs
  )
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const rights: FileRights = useMemo(() => {
    const editAllowed = !!moduleDocs?.project_doc_edit
    const deleteAllowed = !!moduleDocs?.project_doc_delete
    const manageOthers = !!moduleDocs?.project_doc_manage_others
    return {
      currentUserId: currentUserId ?? "",
      editAllowed,
      editForeignAllowed: editAllowed && manageOthers,
      deleteAllowed,
      deleteForeignAllowed: deleteAllowed && manageOthers,
      manageOthersAllowed: manageOthers
    }
  }, [moduleDocs, currentUserId])

  const [documents, setDocuments] = useState<FileItem[]>([])
  const [photos, setPhotos] = useState<FileItem[]>([])
  const [documentsDone, setDocumentsDone] = useState(false)
  const [photosDone, setPhotosDone] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [failed, setFailed] = useState(false)
  const pageRef = useRef(1)
  const requestIdRef = useRef(0)

  const { after, before } = resolveDateBounds({
    date_range: filters.dateRange,
    date_after: filters.dateAfter,
    date_before: filters.dateBefore
  })

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      const requestId = ++requestIdRef.current
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const wantsDocuments = source !== "photos"
      const wantsPhotos = source !== "documents"
      const customer = filters.customerIds.length
        ? filters.customerIds.join(",")
        : undefined
      // Only a genuinely unscoped call (no specific record) should narrow by
      // the selected branch — a per-project/lead caller already names its
      // one workspace via `lead`/`project`, and additionally narrowing THAT
      // by whichever branch happens to be active in the sidebar could 404 a
      // record that legitimately lives on a different, equally-authorized
      // branch.
      const team =
        filters.recordId || !activeBranchId ? undefined : activeBranchId

      const [documentsResponse, photosResponse] = await Promise.all([
        wantsDocuments
          ? getProjectDocuments({
              page,
              page_size: PAGE_SIZE,
              q: filters.q || undefined,
              document_type: filters.documentType || undefined,
              status: filters.status || undefined,
              related_record_type: filters.relatedRecordType || undefined,
              location: filters.location || undefined,
              lead: filters.recordId || undefined,
              customer,
              attention: filters.attention || undefined,
              ordering: documentOrderingFor(sort),
              team
            })
          : null,
        wantsPhotos
          ? getAttachments({
              page,
              page_size: PAGE_SIZE,
              q: filters.q || undefined,
              document_type: filters.documentType || undefined,
              status: filters.status || undefined,
              location: filters.location || undefined,
              project: filters.recordId || undefined,
              customer,
              attention: filters.attention || undefined,
              ordering: photoOrderingFor(sort),
              team
            })
          : null
      ])

      if (requestId !== requestIdRef.current) return

      if (
        (wantsDocuments && !documentsResponse?.ok) ||
        (wantsPhotos && !photosResponse?.ok)
      ) {
        setFailed(true)
        setLoading(false)
        setLoadingMore(false)
        return
      }
      setFailed(false)

      // `ok` above rules out the error-envelope branch of `.data`'s union
      // type at runtime, but apisauce's typing can't express that
      // narrowing, so the success shape is asserted explicitly here.
      const documentsData = documentsResponse?.data as
        | BasePagination<ProjectDocument>
        | undefined
      const photosData = photosResponse?.data as AttachmentListData | undefined
      const documentRows = documentsData?.results ?? []
      const photoRows = photosData
        ? Array.isArray(photosData)
          ? photosData
          : photosData.results
        : []

      const mappedDocuments = documentRows
        .filter((d) => !after || d.created >= after)
        .filter((d) => !before || d.created <= before)
        .map((d) => mapDocumentToFileItem(d, rights))
      const mappedPhotos = photoRows
        .filter((p) => !after || p.created >= after)
        .filter((p) => !before || p.created <= before)
        .map((p) => mapAttachmentToFileItem(p, rights))

      setDocuments((prev) =>
        append ? [...prev, ...mappedDocuments] : mappedDocuments
      )
      setPhotos((prev) => (append ? [...prev, ...mappedPhotos] : mappedPhotos))
      if (wantsDocuments) {
        setDocumentsDone(!documentsData?.next)
      }
      if (wantsPhotos) {
        setPhotosDone(
          photosData
            ? Array.isArray(photosData)
              ? true
              : !photosData.next
            : true
        )
      }
      setLoading(false)
      setLoadingMore(false)
    },
    [
      source,
      filters.q,
      filters.documentType,
      filters.status,
      filters.relatedRecordType,
      filters.location,
      filters.recordId,
      filters.customerIds,
      filters.attention,
      after,
      before,
      sort,
      rights,
      activeBranchId
    ]
  )

  useEffect(() => {
    pageRef.current = 1
    void fetchPage(1, false)
  }, [fetchPage])

  const { dateFormat, timezone } = useDateFormatter()
  const items = useMemo(
    () =>
      sortFileItems([...documents, ...photos], sort, { dateFormat, timezone }),
    [documents, photos, sort, dateFormat, timezone]
  )

  const hasMore =
    (source !== "photos" && !documentsDone) ||
    (source !== "documents" && !photosDone)

  const loadMore = () => {
    if (loadingMore || !hasMore) return
    pageRef.current += 1
    void fetchPage(pageRef.current, true)
  }

  // A write action (rename, status/document-type change, delete, ...) needs
  // to refresh the currently-loaded page(s) in place — unlike `loadMore`,
  // this resets back to page 1 rather than appending.
  const reload = useCallback(() => {
    pageRef.current = 1
    return fetchPage(1, false)
  }, [fetchPage])

  return {
    items,
    loading,
    loadingMore,
    failed,
    hasMore,
    loadMore,
    reload,
    rights
  }
}

export type { GlobalFilesDataFilters, GlobalFilesSource }
export { EMPTY_FILTERS as EMPTY_GLOBAL_FILES_FILTERS }
